/**
 * The Listen tile's queue (M6a step 12) — a pure reducer over "which line of this scene are we
 * on", with no player and no network in it.
 *
 * `lib/tts-player.ts` is single-track by construction: one `HTMLAudioElement`, one Media Session
 * claim, one now-playing bar. Playing a scene back-to-back therefore means chaining, and the
 * chaining logic is the part worth holding still — which line is next, what to prefetch, and the
 * one distinction the whole thing turns on:
 *
 *   **the queue advances on `ended` and halts on `stop`.**
 *
 * Those are the same idle state to the player's ordinary subscribers, which is why `ttsPlayer`
 * grew a separate `onEnded` edge. The bar's ✕ and the lock screen's stop button both call
 * `ttsPlayer.stop()` directly, and a queue that could not tell them from a finished clip would
 * start the next line — audio the learner has just tried to turn off and cannot.
 */

export interface QueueLine {
  lineId: string;
  text: string;
  startMs: number;
  endMs: number;
  /**
   * M6b: which voice speaks this line. A scene from a transcript has one (Listen passes none and
   * the server resolves the default); a dialogue piece has one PER SPEAKER, which is the whole
   * point of the kind. It rides on the line rather than on the queue because the queue's job is
   * "what plays next" and a two-hander changes voice on every turn.
   *
   * The synthesis request carries it straight through to `voiceFor`, which refuses a voice it
   * does not know — so an unmapped speaker is a 422 at play time rather than silence. Publishing
   * already refuses a piece that could reach that state.
   */
  voice?: string;
}

export type QueueStatus = 'idle' | 'playing' | 'halted' | 'done';

export interface QueueState {
  lines: QueueLine[];
  /** which line is current; equals `lines.length` only when `status` is 'done' */
  idx: number;
  status: QueueStatus;
  /** lineId → the audio URL, once the server has synthesized it */
  ready: Record<string, string>;
}

/** How far ahead to synthesize. Four lines is a few seconds of headroom on a slow connection
 *  and a handful of requests, not a whole scene fetched before the first word plays. */
export const PREFETCH_AHEAD = 4;

export function initQueue(lines: QueueLine[]): QueueState {
  return { lines, idx: 0, status: lines.length === 0 ? 'done' : 'idle', ready: {} };
}

export const currentLine = (s: QueueState): QueueLine | null => s.lines[s.idx] ?? null;

/** The URL for the current line, when it has been synthesized. */
export function currentUrl(s: QueueState): string | null {
  const line = currentLine(s);
  return line ? (s.ready[line.lineId] ?? null) : null;
}

export function start(s: QueueState): QueueState {
  if (s.lines.length === 0) return { ...s, status: 'done' };
  return { ...s, status: 'playing' };
}

/**
 * A clip finished on its own. Walks forward; the queue is done when it runs off the end.
 *
 * A no-op unless we were playing: an `ended` arriving after a halt is the element's own tail,
 * and honouring it would restart a queue the learner stopped.
 */
export function ended(s: QueueState): QueueState {
  if (s.status !== 'playing') return s;
  const next = s.idx + 1;
  if (next >= s.lines.length) return { ...s, idx: s.lines.length, status: 'done' };
  return { ...s, idx: next };
}

/** ⏭ — the same walk, but the learner asked, so it works from a halted queue too. */
export function skipNext(s: QueueState): QueueState {
  const next = s.idx + 1;
  if (next >= s.lines.length) return { ...s, idx: s.lines.length, status: 'done' };
  return { ...s, idx: next, status: 'playing' };
}

/** ⏮ — back one line, floored at the first. Never wraps: a scene has a beginning. */
export function skipPrev(s: QueueState): QueueState {
  return { ...s, idx: Math.max(0, s.idx - 1), status: 'playing' };
}

/** The learner stopped it. Terminal until something starts it again — see the header. */
export function halt(s: QueueState): QueueState {
  if (s.status === 'halted' || s.status === 'done') return s;
  return { ...s, status: 'halted' };
}

/** The server answered with a synthesized line. */
export function markReady(s: QueueState, lineId: string, url: string): QueueState {
  if (s.ready[lineId] === url) return s;
  return { ...s, ready: { ...s.ready, [lineId]: url } };
}

/**
 * The lines to ask the server for now: the current one and the next few, minus what is already
 * in hand. Returned in play order so the current line is requested first — a prefetch that
 * fetched line 5 before line 1 would leave the learner waiting with four clips ready.
 */
export function toPrefetch(s: QueueState, ahead = PREFETCH_AHEAD): QueueLine[] {
  if (s.status === 'done') return [];
  const out: QueueLine[] = [];
  for (let i = s.idx; i < Math.min(s.lines.length, s.idx + ahead + 1); i++) {
    const line = s.lines[i]!;
    if (!s.ready[line.lineId]) out.push(line);
  }
  return out;
}

/** How far through the scene, 0..1 — the only progress this screen shows. */
export function queueProgress(s: QueueState): number {
  if (s.lines.length === 0) return 1;
  return Math.min(1, s.idx / s.lines.length);
}
